import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  ErrorMessage,
  Field,
  FieldArray,
  Form,
  Formik,
} from 'formik';
import * as yup from 'yup';
import {
  Row,
  Col,
  ControlLabel,
  FormGroup,
  FormControl,
  Alert,
  Button,
  InputGroup,
  Checkbox,
} from 'react-bootstrap';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import PageLoading from '../../components/PageLoading/PageLoading';
import {
  Portlet,
  PortletBody,
  PortletCaption,
  PortletTitle,
} from '../../components/Bootstrap/Portlet';
import { withTranslation } from 'react-i18next';
import { AdjustedMixDesignQtyType } from '../../constants/AdjustedMixDesignQtyType';
import Tabs from 'react-bootstrap/lib/Tabs';
import Tab from 'react-bootstrap/lib/Tab';
import AuditLog from '../../components/AuditLog/AuditLog';
import startCase from 'lodash/startCase';
import { getQuantityTypeUnitForAdjustedMix } from '../Batch/utils/mix-design-utils';

const AUDIT_LOG_MODEL_NAME = 'AdjustedMixDesign';

class AdjustedMixForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
    };
    this.anchor = React.createRef();
  }

  componentDidMount () {
    this.anchor.current.scrollIntoView();
  }

  renderMixDesign(values, setValues, handleChange) {
    const { materialStore, mixStore, notifierStore, settingsStore, t } = this.props;
    const { assignedMaterials } = materialStore;
    const filteredMaterials = assignedMaterials.filter(v => !values.constituentItems.find(x => x.constituentItemId === v.itemId));
    const qtyTypes = Object.keys(AdjustedMixDesignQtyType).map(v => ({value: v}));

    const onConstituentChange = (e, index, values, setValues) => {
      const constituentItemId = Number(e.target.value);
      const material = assignedMaterials.find(v => v.itemId === constituentItemId);
      const materialDetail = materialStore.materialDetails[material.materialDetailId];
      let payload = { ...values };

      payload.constituentItems[index] = {
        ...values.constituentItems[index],
        constituentItemId,
        constituentItemCode: material.code,
        unitSymbol: materialDetail.itemBatchUnitSymbol,
        mixItemId: mixStore.selectedAdjustedProductId,
        byCement: materialDetail.byCement,
        quantityType: AdjustedMixDesignQtyType.Nominal,
      };

      setValues(payload);
    };

    const onQtyTypeChange = (e, index, values, setValues) => {
      let quantityType = e.target.value;
      let payload = { ...values };
      const constituent = payload.constituentItems[index];

      if (
        quantityType === AdjustedMixDesignQtyType.SecondWater
        && !materialStore.isSecondWater(constituent?.constituentItemId)
      ) {
        quantityType = constituent?.quantityType || AdjustedMixDesignQtyType.Nominal;
        notifierStore.showWarning('The constituent type must be Second Water');
      }

      if (
        [AdjustedMixDesignQtyType.Ice, AdjustedMixDesignQtyType.IcePerVolume].includes(quantityType)
        && !(materialStore.isSecondWater(constituent?.constituentItemId) || materialStore.isWater(constituent?.constituentItemId))
      ) {
        quantityType = constituent?.quantityType || AdjustedMixDesignQtyType.Nominal;
        notifierStore.showWarning('The constituent type must be either Water or Second Water');
      }

      payload.constituentItems[index] = {
        ...constituent,
        quantityType,
      };

      setValues(payload);
    };

    return (
      <React.Fragment>
        <ControlLabel>{t('Mix Design')}</ControlLabel>
        <FieldArray
          name="constituentItems"
          render={arrayHelpers => (
            <div className={'mix-design-form'} ref={this.anchor}>
              {values.constituentItems && values.constituentItems.length > 0 ? (
                values.constituentItems.map((item, index) => (

                  <Row key={index}>
                    <Col sm={3} xs={5}>
                      <FormGroup>
                        <ControlLabel>{t('Constituent')}</ControlLabel>
                        <FormControl
                          name={`constituentItems.${index}.constituentItemId`}
                          componentClass="select"
                          value={values.constituentItems[index].constituentItemId || ''}
                          onChange={e => onConstituentChange(e, index, values, setValues)}
                          bsSize="sm"
                        >
                          {filteredMaterials.map(option => (
                            <option key={option.itemId} value={option.itemId}>
                              {option.code} {option.itemShortDescription}
                            </option>
                          ))}
                          <option value={values.constituentItems[index].constituentItemId || ''}>
                            {values.constituentItems[index].constituentItemCode} {values.constituentItems[index].constituentItemShortDescription}
                          </option>
                        </FormControl>
                        <ErrorMessage component="span" name={`constituentItems.${index}.constituentItemId`} className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={5}>
                      <FormGroup>
                        <ControlLabel>{t('Qty')}</ControlLabel>
                        <InputGroup>
                          <Field name={`constituentItems.${index}.quantity`}
                            placeholder="0"
                            type="number"
                            value={values.constituentItems[index].quantity || ''}
                            className="form-control input-sm" />
                          {item.unitSymbol && (
                            <InputGroup.Addon>
                              {getQuantityTypeUnitForAdjustedMix(item, settingsStore)}
                            </InputGroup.Addon>
                          )}
                        </InputGroup>
                        <ErrorMessage component="span" name={`constituentItems.${index}.quantity`} className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={5}>
                      <FormGroup>
                        <ControlLabel>{t('Qty Type')}</ControlLabel>
                        <FormControl
                          name={`constituentItems.${index}.quantityType`}
                          componentClass="select"
                          value={values.constituentItems[index].quantityType || ''}
                          onChange={(e) => onQtyTypeChange(e, index, values, setValues)}
                          bsSize="sm"
                        >
                          {qtyTypes.map(option => (
                            <option key={option.value} value={option.value}>
                              {startCase(option.value)}
                            </option>
                          ))}
                        </FormControl>
                        <ErrorMessage component="span" name={`constituentItems.${index}.quantityType`} className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={2} xs={3}>
                      <ControlLabel>{t('By Cement')}</ControlLabel>
                      <Checkbox
                        inline
                        className="mt-checkbox mt-checkbox-outline"
                        name={`constituentItems.${index}.byCement`}
                        value={values.constituentItems[index].byCement}
                        checked={!!values.constituentItems[index].byCement}
                        onChange={handleChange}
                        disabled={!values.constituentItems[index]?.constituentItemId || !materialStore.isAdmixture(values.constituentItems[index]?.constituentItemId)}
                      >
                        <span />
                      </Checkbox>
                    </Col>
                    <Col sm={1} xs={1}>
                      <a className="btn btn-sm btn-default btn-action"
                        title="Remove"
                        onClick={() => arrayHelpers.remove(index)}>
                        <i className="fa fa-remove"/>
                      </a>
                    </Col>
                  </Row>

                ))
              ) : '' }

              <Row>
                <Col sm={3}>
                  <a className="btn btn-sm btn-default btn-add"
                    title="Add mix design" onClick={() => arrayHelpers.push({
                      constituentItemId: '',
                      quantity: 0,
                      dosQuantity: 0,
                      order: '',
                    })}>
                    <i className="fa fa-plus"/>
                  </a>
                </Col>
              </Row>
            </div>
          )}
        />
      </React.Fragment>
    );
  }

  renderForm() {
    const  { mixStore, materialStore, notifierStore, onCancel, t } = this.props;
    const { selectedAdjustedProductId, addAdjustedMix, updateAdjustedMix, allExtra, adjustedMixDesigns } = mixStore;
    const { unit } = materialStore;

    const handleItemSelect = (e, values, setValues) => {
      const extraProductItemId = e.target.value;
      const extra = allExtra.find(v => v.itemId === Number(extraProductItemId));

      setValues({
        ...values,
        id: values.id,
        extraProductItemId,
        extraProductItemCode: extra ? extra.itemCode : '',
        extraProductItemDescription: extra ? extra.itemDescription : '',
        unitCode: extra ? extra.unitCode : '',
      });
    };

    const getExtraProductsFiltered = () => {
      return selectedAdjustedProductId
        ? allExtra
        : allExtra.filter(v => !adjustedMixDesigns.find(x => x.extraProductItemId === v.itemId));
    };

    return (
      <div>
        <Formik
          enableReinitialize
          initialValues={
            !selectedAdjustedProductId
              ? this.initialValues()
              : this.getAdjustedMix()
          }
          validationSchema={yup.object().shape({
            extraProductItemId: yup.string().required('Required!'),
            constituentItems: yup.array().of(
              yup.object().shape({
                constituentItemId: yup.string().required('Required!'),
              })
            ),
          })}
          onSubmit={(values, actions) => {
            this.setState({
              error: null,
            });
            let promise;
            if (values.id) {
              promise = updateAdjustedMix(values);
            } else {
              promise = addAdjustedMix(values);
            }

            promise
              .then(() => {
                notifierStore.showSuccess('Saved');
                mixStore.selectAdjustedProduct(null);
                actions.resetForm({
                  values: this.initialValues(),
                });
                actions.setSubmitting(false);
              })
              .catch((e) => {
                this.setState({
                  error: t('Couldn\'t save', { error: e.message }),
                });
                actions.setValues(values);
                actions.setSubmitting(false);
              });

          }}
        >{({ values,
            setValues,
            errors,
            touched,
            isSubmitting,
            handleChange,
          }) => (
            <Form className={'mix-form'}>
              {isSubmitting &&
              <PageLoading/>
              }
              <Row>
                <Col sm={3} xs={6}>
                  <FormGroup className={errors.extraProductItemId && touched.extraProductItemId? 'has-error' : ''}>
                    <ControlLabel>{t('Extra product that adjust')}</ControlLabel>
                    <FormControl
                      name="extraProductItemId"
                      componentClass="select"
                      disabled={!!values.id}
                      value={values.extraProductItemId || ''}
                      onChange={e => handleItemSelect(e, values, setValues)}
                      bsSize="sm"
                    >
                      <option value=""/>
                      {getExtraProductsFiltered().map(option => (
                        <option key={option.itemId} value={option.itemId}>
                          {option.itemCode} {option.itemDescription}
                        </option>
                      ))}
                    </FormControl>
                    <ErrorMessage component="span" name="extraProductItemId" className="help-block" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel>{t('Code')}</ControlLabel>
                    <Field name="extraProductItemCode"
                      placeholder="Code"
                      disabled={true}
                      value={values.extraProductItemCode || ''}
                      className="form-control input-sm" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel>{t('Description')}</ControlLabel>
                    <Field name="extraProductItemDescription"
                      placeholder="Description"
                      disabled={true}
                      value={values.extraProductItemDescription || ''}
                      className="form-control input-sm" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup className={errors.unitCode && touched.unitCode? 'has-error' : ''}>
                    <ControlLabel>{t('Unit')}</ControlLabel>
                    <FormControl
                      name="unitCode"
                      componentClass="select"
                      disabled={true}
                      value={values.unitCode || ''}
                      bsSize="sm"
                    >
                      <option value=""/>
                      {unit.map(option => (
                        <option key={option.id} value={option.code}>
                          {option.symbol || option.name} {option.description}
                        </option>
                      ))}
                    </FormControl>
                    <ErrorMessage component="span" name="unitCode" className="help-block" />
                  </FormGroup>
                </Col>
              </Row>

              {this.renderMixDesign(values, setValues, handleChange)}

              <Row>
                <Col sm={12} className="text-right">
                  {!!this.state.error && <Alert bsStyle="danger">{this.state.error}</Alert>}
                  <Button onClick={onCancel}>{t('Cancel')}</Button>
                  {' '}
                  <LoadingButton
                    bsStyle="primary"
                    type="submit"
                    label={t('Save')}
                    loadingLabel={t('Save')}
                    isLoading={isSubmitting}
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    );
  }

  initialValues() {
    return {
      extraProductItemId: '',
      extraProductItemCode: '',
      extraProductItemDescription: '',
      constituentItems: [],
    };
  };

  getAdjustedMix() {
    const  { mixStore } = this.props;
    const { selectedAdjustedProductId, getAdjustedMixById,  allExtra } = mixStore;
    const adjustedMix = getAdjustedMixById(selectedAdjustedProductId);
    const mix = allExtra.find(v => v.itemId === adjustedMix.extraProductItemId);
    return {
      ...this.initialValues(),
      ...adjustedMix,
      unitCode: mix ? mix.unitCode : '',
    };
  };

  render() {
    const  { mixStore, t } = this.props;
    const { selectedAdjustedProductId } = mixStore;

    return (
      <Portlet>
        <PortletTitle>
          <PortletCaption>
            {t('Adjusted Mix Design')} {selectedAdjustedProductId ? this.getAdjustedMix().extraProductItemCode : ''}
          </PortletCaption>
        </PortletTitle>
        <PortletBody>
          <Tabs
            id={'adjusted-mix--settings-tabs'}
            defaultActiveKey={1}
          >
            <Tab
              eventKey={1}
              title={t('Settings')}
            >
              {this.renderForm()}
            </Tab>
            <Tab
              eventKey={2}
              disabled={!selectedAdjustedProductId}
              title={t('Audit Log')}
            >
              {selectedAdjustedProductId && (
                <AuditLog
                  modelId={selectedAdjustedProductId}
                  modelName={AUDIT_LOG_MODEL_NAME}
                />
              )}
            </Tab>
          </Tabs>
        </PortletBody>
      </Portlet>
    );
  }
}

export default withTranslation()(inject('mixStore', 'materialStore', 'notifierStore', 'settingsStore')(observer(AdjustedMixForm)));
